import { useMantineTheme } from '@mantine/core'
import { useMediaQuery, useMounted } from '@mantine/hooks'

import { Device, ResponsiveBreakpoints } from '@/configs/responsive'
import { useSsrUserAgentDevice } from '@/contexts/ssr'

/**
 * Return the current responsive breakpoint based on the viewport width.
 * Return `undefined` if the default breakpoint is not provided in SSR.
 */
export function useResponsiveBreakpoint(defaultBreakpoint?: ResponsiveBreakpoints) {
  const theme = useMantineTheme()

  const xlargeMatches = useMediaQuery(`(min-width: ${theme.breakpoints.xlarge})`)
  const largeMatches = useMediaQuery(`(min-width: ${theme.breakpoints.large})`)
  const midScaleMatches = useMediaQuery(`(min-width: ${theme.breakpoints.midScale})`)
  const mediumMatches = useMediaQuery(`(min-width: ${theme.breakpoints.medium})`)
  const smallMatches = useMediaQuery(`(min-width: ${theme.breakpoints.small})`)

  const breakpointMatchesPair: Array<[ResponsiveBreakpoints, boolean | undefined]> = [
    [ResponsiveBreakpoints.XLarge, xlargeMatches],
    [ResponsiveBreakpoints.Large, largeMatches],
    [ResponsiveBreakpoints.MidScale, midScaleMatches],
    [ResponsiveBreakpoints.Medium, mediumMatches],
    [ResponsiveBreakpoints.Small, smallMatches],
  ]

  const targetPair = breakpointMatchesPair.find(([, matched]) => matched)

  return targetPair ? targetPair[0] : defaultBreakpoint
}

/**
 * Render mobile view when:
 *  1. The request user agent is mobile in SSR and client hydration
 *  2. The viewport width matches the `small` breakpoint after mounted in client
 */
export const useIsResponsiveMobileView = (defaultBreakpoint?: ResponsiveBreakpoints) => {
  const isMobileSsr = useSsrUserAgentDevice() === Device.Mobile
  const isSmallBreakpoint =
    useResponsiveBreakpoint(defaultBreakpoint) === ResponsiveBreakpoints.Small

  const isMounted = useMounted()
  return !isMounted ? isMobileSsr : isSmallBreakpoint
}

////this function is only used for OG iframe when embeded
export const useIsTinyScreen = (maxWidth = 409) => {
  // SSR guess: only treat as tiny if UA is Mobile
  const isTinySsr = useSsrUserAgentDevice() === Device.Mobile

  // Client live value (updates on resize)
  const isTinyClient = useMediaQuery(`(max-width: ${maxWidth}px)`, false)

  const mounted = useMounted()

  return !mounted ? isTinySsr : isTinyClient
}

/**
 * Render tablet view when:
 *  1. The request user agent is tablet in SSR and client hydration
 *  2. The viewport width matches the `medium` breakpoint after mounted in client
 * NOTE: The `medium` breakpoint is not strictly mapped to the tablets.
 */
export const useIsResponsiveTabletView = (defaultBreakpoint?: ResponsiveBreakpoints) => {
  const isTabletSsr = useSsrUserAgentDevice() === Device.Tablet
  const isMediumBreakpoint =
    useResponsiveBreakpoint(defaultBreakpoint) === ResponsiveBreakpoints.Medium

  const isMounted = useMounted()
  return !isMounted ? isTabletSsr : isMediumBreakpoint
}

/**
 * Render large display view when:
 *  1. The request user agent is desktop in SSR and client hydration
 *  2. The viewport width matches the `large` breakpoint after mounted in client
 */
export const useIsResponsiveLargeDisplayView = (defaultBreakpoint?: ResponsiveBreakpoints) => {
  const isDesktopSsr = useSsrUserAgentDevice() === Device.Desktop
  const isLargeBreakpoint =
    useResponsiveBreakpoint(defaultBreakpoint) === ResponsiveBreakpoints.Large

  const isMounted = useMounted()
  return !isMounted ? isDesktopSsr : isLargeBreakpoint
}

/**
 * Render full display view when:
 *  1. The request user agent is desktop in SSR and client hydration
 *  2. The viewport width matches the `xlarge` breakpoint after mounted in client
 */
export const useIsResponsiveFullDisplayView = (defaultBreakpoint?: ResponsiveBreakpoints) => {
  const isDesktopSsr = useSsrUserAgentDevice() === Device.Desktop
  const isXLargeBreakpoint =
    useResponsiveBreakpoint(defaultBreakpoint) === ResponsiveBreakpoints.XLarge

  const isMounted = useMounted()
  return !isMounted ? isDesktopSsr : isXLargeBreakpoint
}
